import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import PasswordInput from '../components/PasswordInput';
import TalitLogo from '../components/TalitLogo';

// Very small heuristic to decide whether the owner-login identifier field
// should be sent as `email` or `phone` to POST /auth/owner-login (which
// requires exactly one of the two). Auto-detected from the input's shape
// rather than a separate toggle control, to keep the form to a single
// field: if it contains an "@" it's treated as an email, otherwise as a
// phone number. This is display/UX convenience only — the backend does its
// own validation regardless of which field we send it under.
function looksLikeEmail(value) {
  return value.includes('@');
}

function StaffLoginForm() {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tenantSlug, setTenantSlug] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(tenantSlug.trim(), username.trim(), password);
      navigate('/app');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div>
        <label htmlFor="tenantSlug" className="block text-sm font-medium text-gray-700 mb-2">
          Hotel ID
        </label>
        <input
          id="tenantSlug"
          name="tenantSlug"
          type="text"
          required
          value={tenantSlug}
          onChange={(e) => setTenantSlug(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="your-hotel-slug"
          disabled={loading}
        />
      </div>

      <div>
        <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
          Username
        </label>
        <input
          id="username"
          name="username"
          type="text"
          required
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter your username"
          disabled={loading}
        />
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
          Password
        </label>
        <PasswordInput
          id="password"
          name="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter your password"
          disabled={loading}
        />
      </div>

      <button
        type="submit"
        disabled={loading || !tenantSlug || !username || !password}
        className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Signing in...' : 'Sign in to Dashboard'}
      </button>

      <p className="text-center text-xs text-gray-700">
        Forgot your password? Ask your hotel's administrator to reset it in User Management,
        or, if you're the business owner,{' '}
        <Link to="/forgot-password" className="text-blue-600 hover:text-blue-800">
          reset it via your owner account
        </Link>
        .
      </p>
    </form>
  );
}

function OwnerLoginForm() {
  const { ownerLogin, chooseBusiness } = useContext(AuthContext);
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [chooser, setChooser] = useState(null); // { token, tenants } | null

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const trimmed = identifier.trim();
      const result = await ownerLogin(trimmed, password, !looksLikeEmail(trimmed));
      if (result.chooseBusiness) {
        setChooser({ token: result.token, tenants: result.tenants });
      } else {
        navigate('/app');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleChoose = async (tenantId) => {
    setError('');
    setLoading(true);
    try {
      await chooseBusiness(chooser.token, tenantId);
      navigate('/app');
    } catch (err) {
      setError(err.message || 'Could not open that business. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (chooser) {
    return (
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        <p className="text-sm text-gray-600">
          Your account manages more than one business. Choose which one to sign in to:
        </p>
        <ul className="space-y-2">
          {chooser.tenants.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                disabled={loading}
                onClick={() => handleChoose(t.id)}
                className="w-full text-left px-4 py-3 border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <div className="font-medium text-gray-900">{t.name}</div>
                <div className="text-xs text-gray-700">{t.slug}</div>
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setChooser(null)}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          &larr; Back
        </button>
      </div>
    );
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div>
        <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-2">
          Email or phone number
        </label>
        <input
          id="identifier"
          name="identifier"
          type="text"
          required
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="you@example.com or +254712345678"
          disabled={loading}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="ownerPassword" className="block text-sm font-medium text-gray-700">
            Password
          </label>
          <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-800">
            Forgot password?
          </Link>
        </div>
        <PasswordInput
          id="ownerPassword"
          name="ownerPassword"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter your password"
          disabled={loading}
        />
      </div>

      <button
        type="submit"
        disabled={loading || !identifier || !password}
        className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Signing in...' : 'Sign in'}
      </button>
    </form>
  );
}

function LoginPage() {
  const [mode, setMode] = useState('staff'); // 'staff' | 'owner'

  return (
    <div className="public-page min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link
            to="/"
            className="mx-auto mb-4 block w-16 rounded-2xl transition-opacity hover:opacity-80"
            aria-label="Back to TALIT PMS home"
          >
            <TalitLogo size={64} className="rounded-2xl" />
          </Link>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
            Sign in
          </h2>
          <p className="text-sm text-gray-600">
            Access your hotel's dashboard
          </p>
          <Link
            to="/"
            className="inline-block mt-2 text-sm text-blue-600 hover:text-blue-800"
          >
            ← Back to home
          </Link>
        </div>

        <div className="bg-white py-8 px-6 shadow-xl rounded-lg">
          <div className="flex mb-6 border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setMode('staff')}
              className={`flex-1 py-2 text-sm font-medium ${
                mode === 'staff' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Sign in as staff
            </button>
            <button
              type="button"
              onClick={() => setMode('owner')}
              className={`flex-1 py-2 text-sm font-medium ${
                mode === 'owner' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Sign in as owner
            </button>
          </div>

          {mode === 'staff' ? <StaffLoginForm /> : <OwnerLoginForm />}

          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-blue-600 hover:text-blue-800 font-medium">
                Register your hotel
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
